import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  connectToJob,
  type JobConnection,
  type WSJobStatusResponse
} from '@/services/websocket'
import { bitable, ViewType } from '@lark-base-open/js-sdk'
import { API_ENDPOINTS } from '@/config/api'

const STORAGE_KEY = 'automadoc_active_jobs'
const MAX_RECORDS = 15

/**
 * Per-generation tracking
 */
export interface GenerationProgress {
  generationId: string
  status: 'pending' | 'processing' | 'uploading' | 'completed' | 'failed' | 'cancelled'
  currentStep?: string
  filename?: string
  downloadUrl?: string
  error?: string
  hadWarnings?: boolean
  warnings?: string[]
}

/**
 * Job progress (one per template)
 */
export interface JobProgress {
  jobId: string | null  // null when status is 'started' (before API response)
  templateId: string
  templateName: string
  active: boolean
  totalRecords: number
  completedCount: number
  failedCount: number
  cancelledCount: number
  warningCount: number
  status: 'started' | 'pending' | 'processing' | 'completed' | 'partial' | 'failed' | 'cancelled'
  generations: GenerationProgress[]
}

/**
 * Persisted job info for localStorage
 */
interface PersistedJob {
  jobId: string
  generationIds: string[]
  templateId: string
  templateName: string
  totalRecords: number
}

export interface GenerationResult {
  success: boolean
  error?: string
  maxRecords?: number
}

export const useGenerationStore = defineStore('generation', () => {
  // Simple array of jobs - Vue handles array reactivity well
  const jobs = ref<JobProgress[]>([])

  // Track WebSocket connections (not reactive, just internal state)
  const connections = new Map<string, JobConnection>()

  // Computed
  const hasAnyProgress = computed(() => jobs.value.length > 0)
  const hasAnyActive = computed(() => jobs.value.some(j => j.active))

  /**
   * Find job by templateId
   */
  function findJob(templateId: string): JobProgress | undefined {
    return jobs.value.find(j => j.templateId === templateId)
  }

  /**
   * Find generation within a job
   */
  function findGeneration(job: JobProgress, generationId: string): GenerationProgress | undefined {
    return job.generations.find(g => g.generationId === generationId)
  }

  /**
   * Check if template has active generation
   */
  function isTemplateGenerating(templateId: string): boolean {
    return findJob(templateId)?.active ?? false
  }

  /**
   * Save active jobs to localStorage
   */
  function persistJobs() {
    const toSave: PersistedJob[] = jobs.value
      .filter(j => j.active && j.jobId !== null)  // Only persist jobs with real jobIds
      .map(j => ({
        jobId: j.jobId!,
        generationIds: j.generations.map(g => g.generationId),
        templateId: j.templateId,
        templateName: j.templateName,
        totalRecords: j.totalRecords,
      }))

    if (toSave.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave))
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }

  /**
   * Update job from WebSocket status - uses immutable update pattern
   */
  function updateJobFromStatus(templateId: string, status: WSJobStatusResponse) {
    const jobIndex = jobs.value.findIndex(j => j.templateId === templateId)
    if (jobIndex === -1) return

    const oldJob = jobs.value[jobIndex]!

    // Build updated generations array (immutable)
    const updatedGenerations = oldJob.generations.map(gen => {
      const genStatus = status.generations.find(g => g.generationId === gen.generationId)
      if (genStatus) {
        return {
          ...gen,
          status: genStatus.status,
          currentStep: genStatus.currentStep,
          filename: genStatus.filename,
          downloadUrl: genStatus.downloadUrl,
          error: genStatus.error,
          hadWarnings: genStatus.hadWarnings,
          warnings: genStatus.warnings,
        }
      }
      return gen
    })

    // Replace entire job object (immutable)
    const updatedJob: JobProgress = {
      jobId: oldJob.jobId,
      templateId: oldJob.templateId,
      templateName: oldJob.templateName,
      active: oldJob.active,
      totalRecords: oldJob.totalRecords,
      status: status.status,
      completedCount: status.completedCount,
      failedCount: status.failedCount,
      cancelledCount: status.cancelledCount,
      warningCount: updatedGenerations.filter(g => g.hadWarnings).length,
      generations: updatedGenerations,
    }

    // Replace in array - this triggers Vue reactivity
    jobs.value = [
      ...jobs.value.slice(0, jobIndex),
      updatedJob,
      ...jobs.value.slice(jobIndex + 1)
    ]
  }

  /**
   * Create WebSocket callbacks for a job
   */
  function createJobCallbacks(templateId: string) {
    return {
      onJobStatus: (status: WSJobStatusResponse) => {
        updateJobFromStatus(templateId, status)
      },

      onGenerationProgress: (generationId: string, message: string) => {
        const job = findJob(templateId)
        if (!job) return
        const gen = findGeneration(job, generationId)
        if (gen) {
          if (gen.status === 'pending') gen.status = 'processing'
          gen.currentStep = message
        }
      },

      onGenerationComplete: (generationId: string, filename?: string, downloadUrl?: string, hadWarnings?: boolean) => {
        const job = findJob(templateId)
        if (!job) return
        const gen = findGeneration(job, generationId)
        if (gen) {
          gen.status = 'completed'
          gen.filename = filename
          gen.downloadUrl = downloadUrl
          gen.hadWarnings = hadWarnings
        }
        if (hadWarnings) job.warningCount++
      },

      onGenerationError: (generationId: string, error?: string) => {
        const job = findJob(templateId)
        if (!job) return
        const gen = findGeneration(job, generationId)
        if (gen) {
          gen.status = 'failed'
          gen.error = error
        }
      },

      onGenerationWarning: (generationId: string, warning: string) => {
        const job = findJob(templateId)
        if (!job) return
        const gen = findGeneration(job, generationId)
        if (gen) {
          gen.hadWarnings = true
          if (!gen.warnings) gen.warnings = []
          gen.warnings.push(warning)
        }
      },

      onGenerationCancelled: (generationId: string) => {
        const job = findJob(templateId)
        if (!job) return
        const gen = findGeneration(job, generationId)
        if (gen) gen.status = 'cancelled'
      },

      onJobComplete: (status: WSJobStatusResponse) => {
        const job = findJob(templateId)
        if (!job) return
        job.active = false
        updateJobFromStatus(templateId, status)
        connections.delete(templateId)
        persistJobs()
      },

      onJobCancelled: (_cancelledCount: number) => {
        const jobIndex = jobs.value.findIndex(j => j.templateId === templateId)
        if (jobIndex === -1) return

        const oldJob = jobs.value[jobIndex]!

        // Mark all pending/processing generations as cancelled
        const updatedGenerations = oldJob.generations.map(gen => {
          if (gen.status === 'pending' || gen.status === 'processing' || gen.status === 'uploading') {
            return { ...gen, status: 'cancelled' as const }
          }
          return gen
        })

        // Update job status (immutable)
        const updatedJob: JobProgress = {
          ...oldJob,
          active: false,
          status: 'cancelled',
          cancelledCount: updatedGenerations.filter(g => g.status === 'cancelled').length,
          generations: updatedGenerations,
        }

        jobs.value = [
          ...jobs.value.slice(0, jobIndex),
          updatedJob,
          ...jobs.value.slice(jobIndex + 1)
        ]

        connections.delete(templateId)
        persistJobs()
      },

      onJobNotFound: () => {
        const job = findJob(templateId)
        if (!job) return
        job.active = false
        job.status = 'failed'
        connections.delete(templateId)
        persistJobs()
      },

      onInfo: (_message: string) => {
        // Could log or display info messages
      }
    }
  }

  /**
   * Get selected record IDs from Lark
   */
  async function getSelectedRecordIds(): Promise<string[]> {
    const selection = await bitable.base.getSelection()
    if (!selection.tableId) return []

    const table = await bitable.base.getTableById(selection.tableId)
    const view = await table.getActiveView()
    const viewType = await view.getType()

    let recordIds: string[] = []

    if (viewType === ViewType.Grid) {
      // @ts-ignore - method exists at runtime
      recordIds = await view.getSelectedRecordIdList()
      if (recordIds.length === 0 && selection.recordId) {
        recordIds = [selection.recordId]
      }
    } else if (selection.recordId) {
      recordIds = [selection.recordId]
    }

    return recordIds
  }

  /**
   * Get auth headers
   */
  async function getAuthHeaders(): Promise<Record<string, string>> {
    const [tenantKey, userId, selection] = await Promise.all([
      bitable.bridge.getTenantKey(),
      bitable.bridge.getUserId(),
      bitable.base.getSelection(),
    ])

    if (!selection.baseId) throw new Error('No base selected')

    return {
      'X-Lark-Tenant-Key': tenantKey,
      'X-Lark-User-Id': userId,
      'X-Lark-Base-Token': selection.baseId,
    }
  }

  /**
   * Start document generation
   */
  async function startGeneration(
    baseToken: string,
    tableId: string,
    template: { id: string; name: string }
  ): Promise<GenerationResult> {
    if (isTemplateGenerating(template.id)) {
      return { success: false, error: 'GENERATION_IN_PROGRESS' }
    }

    const selectedRecordIds = await getSelectedRecordIds()
    if (selectedRecordIds.length === 0) {
      return { success: false, error: 'NO_RECORDS_SELECTED' }
    }

    // Enforce record limit on frontend
    if (selectedRecordIds.length > MAX_RECORDS) {
      return { success: false, error: 'TOO_MANY_RECORDS', maxRecords: MAX_RECORDS }
    }

    // Re-check after async operations to prevent race condition
    if (isTemplateGenerating(template.id)) {
      return { success: false, error: 'GENERATION_IN_PROGRESS' }
    }

    // Clean up any previous inactive job for this template (e.g., completed/failed jobs)
    const existingJob = findJob(template.id)
    if (existingJob) {
      // Close any lingering connection
      const conn = connections.get(template.id)
      if (conn) {
        conn.close()
        connections.delete(template.id)
      }
      // Only remove if not active (active jobs blocked by check above)
      if (!existingJob.active) {
        const idx = jobs.value.findIndex(j => j.templateId === template.id)
        if (idx !== -1) {
          jobs.value = [
            ...jobs.value.slice(0, idx),
            ...jobs.value.slice(idx + 1)
          ]
        }
      }
    }

    // Add job immediately with 'started' status for instant UI feedback
    const startedJob: JobProgress = {
      jobId: null,
      templateId: template.id,
      templateName: template.name,
      active: true,
      totalRecords: selectedRecordIds.length,
      completedCount: 0,
      failedCount: 0,
      cancelledCount: 0,
      warningCount: 0,
      status: 'started',
      generations: [],
    }
    jobs.value = [...jobs.value, startedJob]

    try {
      const authHeaders = await getAuthHeaders()

      const response = await fetch(
        API_ENDPOINTS.generate(baseToken, tableId, template.id),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ recordIds: selectedRecordIds }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
      }

      const result = await response.json() as {
        jobId: string
        generationIds: string[]
        total: number
        initialProgress?: Array<{ generationId: string; progress: string[] }>
      }

      // Build progress map from initial progress
      const progressMap = new Map<string, string[]>()
      if (result.initialProgress) {
        for (const p of result.initialProgress) {
          progressMap.set(p.generationId, p.progress)
        }
      }

      // Create generation entries with initial progress
      const generations: GenerationProgress[] = result.generationIds.map(genId => {
        const progress = progressMap.get(genId) || []
        return {
          generationId: genId,
          status: 'pending' as const,
          currentStep: progress.length > 0 ? progress[progress.length - 1] : undefined,
        }
      })

      // Update job with real data from API response
      const jobIndex = jobs.value.findIndex(j => j.templateId === template.id)
      if (jobIndex !== -1) {
        const updatedJob: JobProgress = {
          jobId: result.jobId,
          templateId: template.id,
          templateName: template.name,
          active: true,
          totalRecords: selectedRecordIds.length,
          completedCount: 0,
          failedCount: 0,
          cancelledCount: 0,
          warningCount: 0,
          status: 'processing',
          generations,
        }
        jobs.value = [
          ...jobs.value.slice(0, jobIndex),
          updatedJob,
          ...jobs.value.slice(jobIndex + 1)
        ]
      }

      persistJobs()

      // Connect WebSocket
      const connection = connectToJob(result.jobId, createJobCallbacks(template.id))
      connections.set(template.id, connection)
      connection.promise.catch(() => {})

      return { success: true }

    } catch (error) {
      // Remove the started job on error
      const jobIndex = jobs.value.findIndex(j => j.templateId === template.id)
      if (jobIndex !== -1) {
        jobs.value = [
          ...jobs.value.slice(0, jobIndex),
          ...jobs.value.slice(jobIndex + 1)
        ]
      }
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Reconnect to existing job after page reload
   */
  function reconnectToJob(
    jobId: string,
    generationIds: string[],
    templateId: string,
    templateName: string,
    totalRecords: number
  ) {
    if (findJob(templateId)) return

    const job: JobProgress = {
      jobId,
      templateId,
      templateName,
      active: true,
      totalRecords,
      completedCount: 0,
      failedCount: 0,
      cancelledCount: 0,
      warningCount: 0,
      status: 'pending',
      generations: generationIds.map(genId => ({
        generationId: genId,
        status: 'pending' as const,
      }))
    }

    jobs.value.push(job)

    const connection = connectToJob(jobId, createJobCallbacks(templateId))
    connections.set(templateId, connection)
    connection.promise.catch(() => {})
  }

  /**
   * Initialize - restore jobs from localStorage
   */
  function init() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        const persisted: PersistedJob[] = JSON.parse(saved)
        for (const p of persisted) {
          // Skip invalid jobs (empty jobId)
          if (!p.jobId) continue
          reconnectToJob(p.jobId, p.generationIds, p.templateId, p.templateName, p.totalRecords)
        }
      }
    } catch (e) {
      console.warn('[Generation Store] Failed to restore jobs:', e)
      localStorage.removeItem(STORAGE_KEY)
    }
  }

  /**
   * Cancel a job
   */
  function cancelGeneration(templateId: string) {
    connections.get(templateId)?.cancel()
  }

  /**
   * Close/dismiss a job's progress
   */
  function closeProgress(templateId: string) {
    const conn = connections.get(templateId)
    if (conn) {
      conn.close()
      connections.delete(templateId)
    }
    const idx = jobs.value.findIndex(j => j.templateId === templateId)
    if (idx !== -1) jobs.value.splice(idx, 1)
    persistJobs()
  }

  /**
   * Close all progress panels
   */
  function closeAllProgress() {
    for (const conn of connections.values()) conn.close()
    connections.clear()
    jobs.value = []
    localStorage.removeItem(STORAGE_KEY)
  }

  return {
    // State
    jobs,

    // Computed
    hasAnyProgress,
    hasAnyActive,

    // Actions
    findJob,
    isTemplateGenerating,
    getSelectedRecordIds,
    startGeneration,
    reconnectToJob,
    init,
    cancelGeneration,
    closeProgress,
    closeAllProgress,
    reset: closeAllProgress
  }
})
